import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EllipsisIcon, PencilIcon, XIcon } from 'lucide-react';
import { Button, ConfirmDialog, ErrorNotice, Field, SelectInput, TextArea, TextInput } from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { errorMessage } from '@/lib/api';
import { contactTypeLabel, createContactType, listContactTypes, type ContactType } from '@/lib/contact-types';
import { createEventContact, deleteEventContact, updateEventContact, type LocalContact, type LocalContactWrite } from '@/lib/events';

/**
 * Hand-added contacts on an event — the venue's AV lead, a DMC, a charity
 * rep — typed by an admin-managed contact type. They sit under the
 * Salesforce contacts on the Overview and never grant portal access.
 */

/** The picker value that opens the inline "new type" field. */
const NEW_TYPE = '__new__';

export function useContactTypes() {
  return useQuery({
    queryKey: ['contact-types'],
    queryFn: ({ signal }) => listContactTypes(signal),
    staleTime: 5 * 60_000,
  });
}

/** One hand-added contact: type label, name, reachable lines, and actions. */
export function LocalContactBlock({
  contact,
  types,
  onEdit,
  onRemove,
}: {
  readonly contact: LocalContact;
  readonly types: readonly ContactType[] | undefined;
  readonly onEdit?: (() => void) | undefined;
  readonly onRemove?: (() => void) | undefined;
}): React.ReactElement {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-portal-micro font-extrabold tracking-[0.6px] text-portal-muted uppercase">
          {contactTypeLabel(types, contact.typeKey)}
          {contact.company ? ` · ${contact.company}` : ''}
        </p>
        {contact.email ? (
          <Link to={`/contacts/by-email/${encodeURIComponent(contact.email)}`} className="mt-1 block text-portal-small font-bold text-brand hover:underline">
            {contact.name}
          </Link>
        ) : (
          <p className="mt-1 text-portal-small font-bold text-portal-ink">{contact.name}</p>
        )}
        <div className="mt-1 space-y-1 text-portal-micro text-portal-muted">
          {contact.email ? <p>{contact.email}</p> : null}
          {contact.phone ? <p>{contact.phone}</p> : null}
          {contact.cell ? <p>{contact.cell} (cell)</p> : null}
          {contact.note ? <p className="text-portal-ink">{contact.note}</p> : null}
        </div>
      </div>
      {onEdit || onRemove ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" data-guide="contact-row-menu" aria-label={`Actions for ${contact.name}`} className="grid size-7 shrink-0 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink">
              <EllipsisIcon aria-hidden className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onEdit ? (
              <DropdownMenuItem onSelect={onEdit}>
                <PencilIcon aria-hidden className="size-4" /> Edit
              </DropdownMenuItem>
            ) : null}
            {onRemove ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onSelect={onRemove}>
                  <XIcon aria-hidden className="size-4" /> Remove
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}

/**
 * The hand-added contacts with add, edit and remove. Renders the rows only
 * (the Overview card owns the separators) plus its dialogs.
 */
export function LocalContacts({
  eventId,
  contacts,
  canEdit,
  isAdmin,
  adding,
  onAddingChange,
}: {
  readonly eventId: string;
  readonly contacts: readonly LocalContact[];
  readonly canEdit: boolean;
  readonly isAdmin: boolean;
  /** The Overview's "Add contact" button drives this. */
  readonly adding: boolean;
  readonly onAddingChange: (open: boolean) => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const types = useContactTypes();
  const [editing, setEditing] = useState<LocalContact | null>(null);
  const [removing, setRemoving] = useState<LocalContact | null>(null);

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
  }
  const remove = useMutation({
    mutationFn: (contact: LocalContact) => deleteEventContact(eventId, contact._id),
    onSuccess: async (_result, contact) => {
      toast.success(`Removed ${contact.name}`);
      setRemoving(null);
      await refresh();
    },
  });

  return (
    <>
      {contacts.map((contact) => (
        <LocalContactBlock
          key={contact._id}
          contact={contact}
          types={types.data}
          onEdit={canEdit ? () => setEditing(contact) : undefined}
          onRemove={canEdit ? () => setRemoving(contact) : undefined}
        />
      ))}

      <ContactDialog
        eventId={eventId}
        open={adding}
        isAdmin={isAdmin}
        onClose={() => onAddingChange(false)}
        onSaved={async () => {
          onAddingChange(false);
          await refresh();
        }}
      />
      {editing ? (
        <ContactDialog
          key={editing._id}
          eventId={eventId}
          existing={editing}
          open
          isAdmin={isAdmin}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      ) : null}
      <ConfirmDialog
        open={removing !== null}
        tone="danger"
        title={`Remove ${removing?.name ?? 'this contact'}?`}
        message="They come off this event only. Nothing in Salesforce changes."
        confirmLabel="Remove"
        pending={remove.isPending}
        error={remove.isError ? errorMessage(remove.error) : undefined}
        onConfirm={() => {
          if (removing) remove.mutate(removing);
        }}
        onCancel={() => {
          if (!remove.isPending) setRemoving(null);
        }}
      />
    </>
  );
}

/** The add/edit form: type (with inline new type for admins), name, reachable fields, note. */
function ContactDialog({
  eventId,
  existing,
  open,
  isAdmin,
  onClose,
  onSaved,
}: {
  readonly eventId: string;
  readonly existing?: LocalContact;
  readonly open: boolean;
  readonly isAdmin: boolean;
  readonly onClose: () => void;
  readonly onSaved: () => Promise<void>;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const types = useContactTypes();
  const active = (types.data ?? []).filter((type) => type.isActive || type.key === existing?.typeKey);
  const [typeKey, setTypeKey] = useState(existing?.typeKey ?? '');
  const [newTypeLabel, setNewTypeLabel] = useState('');
  const [name, setName] = useState(existing?.name ?? '');
  const [email, setEmail] = useState(existing?.email ?? '');
  const [phone, setPhone] = useState(existing?.phone ?? '');
  const [cell, setCell] = useState(existing?.cell ?? '');
  const [company, setCompany] = useState(existing?.company ?? '');
  const [note, setNote] = useState(existing?.note ?? '');
  const resolvedType = typeKey || active[0]?.key || '';

  const save = useMutation({
    mutationFn: async () => {
      let key = resolvedType;
      if (key === NEW_TYPE) {
        const created = await createContactType({ label: newTypeLabel.trim() });
        await queryClient.invalidateQueries({ queryKey: ['contact-types'] });
        key = created.key;
      }
      const body: LocalContactWrite = {
        typeKey: key,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        cell: cell.trim(),
        company: company.trim(),
        note: note.trim(),
      };
      return existing ? updateEventContact(eventId, existing._id, body) : createEventContact(eventId, body);
    },
    onSuccess: async () => {
      toast.success(existing ? `Saved ${name.trim()}` : `Added ${name.trim()}`);
      await onSaved();
    },
  });

  const canSave =
    name.trim().length > 0 && resolvedType !== '' && (resolvedType !== NEW_TYPE || newTypeLabel.trim().length > 0) && !save.isPending;

  return (
    <Dialog open={open} onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-lg">
        <DialogTitle>{existing ? 'Edit contact' : 'Add contact'}</DialogTitle>
        <form
          className="mt-2 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSave) save.mutate();
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field guide="contact-type" label="Type" htmlFor="contact-type" required>
              <SelectInput id="contact-type" value={resolvedType} onChange={(event) => setTypeKey(event.target.value)}>
                {active.map((type) => (
                  <option key={type.key} value={type.key}>
                    {type.label}
                  </option>
                ))}
                {isAdmin ? <option value={NEW_TYPE}>New type…</option> : null}
              </SelectInput>
            </Field>
            {resolvedType === NEW_TYPE ? (
              <Field label="New type name" htmlFor="contact-new-type">
                <TextInput id="contact-new-type" value={newTypeLabel} placeholder="Photographer" onChange={(event) => setNewTypeLabel(event.target.value)} />
              </Field>
            ) : (
              <Field guide="contact-company" label="Company" htmlFor="contact-company">
                <TextInput id="contact-company" value={company} onChange={(event) => setCompany(event.target.value)} />
              </Field>
            )}
          </div>
          <Field guide="contact-name" label="Name" htmlFor="contact-name" required>
            <TextInput id="contact-name" value={name} maxLength={120} onChange={(event) => setName(event.target.value)} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Email" htmlFor="contact-email">
              <TextInput id="contact-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
            </Field>
            <Field label="Phone" htmlFor="contact-phone">
              <TextInput id="contact-phone" type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} />
            </Field>
            <Field label="Cell" htmlFor="contact-cell">
              <TextInput id="contact-cell" type="tel" value={cell} onChange={(event) => setCell(event.target.value)} />
            </Field>
          </div>
          <Field guide="contact-note" label="Note" htmlFor="contact-note" hint="Hours, how they prefer to be reached, what they own on the day.">
            <TextArea id="contact-note" rows={2} value={note} maxLength={1000} onChange={(event) => setNote(event.target.value)} />
          </Field>
          <p className="text-portal-micro text-portal-muted">Contacts added here do not get client-portal access; that still comes from Salesforce.</p>
          {save.isError ? <ErrorNotice inline title="Contact not saved" message={errorMessage(save.error)} /> : null}
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSave}>
              {save.isPending ? 'Saving…' : existing ? 'Save contact' : 'Add contact'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
